import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, CTABand, FAQ } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import journeyHero from "@/assets/hero/hero-flip-renovation.jpg";

const URL = "/investor-journey";
const TITLE = "Bridge → Fix & Flip → DSCR Refi | Simply Approved";
const DESCRIPTION =
  "One lender for the full BRRRR cycle. Bridge to acquire, Fix & Flip to reposition, and DSCR to refinance and hold — same file, same team, no re-underwriting from scratch.";

const KEYWORDS = [
  "BRRRR financing",
  "bridge to DSCR refinance",
  "fix and flip to DSCR",
  "investor loan journey",
  "buy rehab rent refinance repeat",
  "bridge loan acquisition strategy",
  "DSCR takeout loan",
  "one lender BRRRR",
  "rental portfolio financing cycle",
  "reused appraisal refinance",
  "cash out DSCR refinance strategy",
].join(", ");

const HOWTO_JSONLD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Bridge → Fix & Flip → DSCR Refinance investor journey",
  description:
    "How real estate investors use bridge, fix & flip, and DSCR loans in sequence to acquire, reposition, and hold rental property.",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Bridge — acquire on your timeline",
      text: "Close on off-market, auction, or 1031 deals in 10–14 days with a business-purpose bridge loan. No income docs, interest-only, 12–24 months.",
      url: "/bridge-loans",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Fix & Flip — reposition the asset",
      text: "Reuse the same file to fund up to 90% of purchase and 100% of rehab on 2-week draws. Renovate to lift ARV and market rent.",
      url: "/fix-and-flip-loans",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "DSCR Refinance — take out and hold",
      text: "Refinance into a 30-year DSCR loan at stabilization. Same lender, reused appraisal where possible, cash-out to fund the next deal.",
      url: "/dscr-loans",
    },
  ],
};

const journeyFaqs = [
  {
    q: "Do I have to use all three products?",
    a: "No. Each program stands on its own. But if your business plan is buy → renovate → hold, running all three through one lender saves time, docs, and third-party costs.",
  },
  {
    q: "Can you reuse the appraisal from bridge to DSCR?",
    a: "In many cases, yes — as long as the appraisal is within the lender's usable window and the scope of work matches the as-stabilized valuation. We flag this at bridge closing.",
  },
  {
    q: "How long does the full cycle take?",
    a: "Typical BRRRR cycles run 6–12 months: 10–14 days to close the bridge, 60–120 days to complete the rehab, 60–90 days of seasoning, then a 15–21 day DSCR takeout.",
  },
  {
    q: "Do I lock the DSCR rate at bridge closing?",
    a: "No — rates float until the takeout is underwritten. We do provide indicative pricing at bridge closing so you can model the exit.",
  },
  {
    q: "Can I cash out at the DSCR takeout?",
    a: "Yes — most stabilized takeouts allow cash-out up to 75% LTV against the new appraised value, giving you capital to acquire the next deal.",
  },
];

export const Route = createFileRoute("/investor-journey")({
  head: () =>
    buildHead({
      title: TITLE,
      description: DESCRIPTION,
      url: URL,
      keywords: KEYWORDS,
      type: "article",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Investor Journey", url: URL },
      ],
      service: { name: "Bridge, Fix & Flip, and DSCR Refinance Lending" },
      faqs: journeyFaqs,
      extraJsonLd: [HOWTO_JSONLD],
    }),
  component: InvestorJourneyPage,
});

const stages = [
  {
    n: "01",
    slug: "bridge-loans",
    tag: "Acquire",
    title: "Bridge Loan",
    subtitle: "Buy now, refinance later.",
    body:
      "Win competitive deals — off-market, auction, 1031 exchange, or distressed acquisitions — with fast, business-purpose bridge capital. No income docs, no waiting on stabilized cash flow.",
    bullets: [
      "10–14 day close",
      "Up to 80% LTV, interest-only",
      "12–24 month term",
      "No prepayment penalty",
    ],
  },
  {
    n: "02",
    slug: "fix-and-flip-loans",
    tag: "Reposition",
    title: "Fix & Flip Loan",
    subtitle: "Fund the rehab, lift the ARV.",
    body:
      "Use the same lender relationship — often the same appraisal — to draw against your renovation budget. Add units, refresh systems, or reposition for a market-rate rent.",
    bullets: [
      "Up to 90% of purchase, 100% of rehab",
      "2-week draw cycle",
      "Interest-only payments",
      "BRRRR-ready file structure",
    ],
  },
  {
    n: "03",
    slug: "dscr-loans",
    tag: "Refinance & hold",
    title: "DSCR Refinance",
    subtitle: "Cash out. Hold long-term.",
    body:
      "Once the property is stabilized and rented, take out the short-term debt with a 30-year DSCR loan. Pull cash out at closing to fund the next acquisition — and start the cycle again.",
    bullets: [
      "30-yr fixed, ARM, or interest-only",
      "Cash-out up to 75% LTV",
      "Reused appraisal where eligible",
      "LLC vesting, no tax returns",
    ],
  },
];

function InvestorJourneyPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="The Investor Journey"
        title="Bridge → Fix & Flip → DSCR Refinance. One lender, one file."
        subtitle="Serious investors don't need one loan — they need three. We fund the full BRRRR cycle from acquisition to long-term hold, without starting over at every step."
        primaryHref="/apply"
        primaryLabel="Start My Approval"
        secondaryHref="/rates"
        secondaryLabel="See Today's Rates"
        image={{
          src: journeyHero,
          alt: "Contractors renovating a kitchen while the property owners review progress on a tablet",
        }}
      />
      <PageEssentials />

      <Section>
        <SectionHeader
          eyebrow="End-to-end investor lending"
          title="The full lifecycle of a rental deal, financed under one roof"
          intro="Most lenders hand you off between products. We keep your file, reuse your appraisal where possible, and price the takeout when we close the bridge — so there are no surprises 12 months in."
          center
        />

        <ol className="mt-14 space-y-8">
          {stages.map((s, i) => (
            <li key={s.slug} className="relative">
              <div className="grid gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm md:grid-cols-[auto_1fr_auto] md:items-start md:gap-8 md:p-10">
                <div className="flex items-center gap-4 md:block">
                  <div className="font-display text-5xl font-bold text-brand-gold md:text-6xl">{s.n}</div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-brand md:mt-2">
                    {s.tag}
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold text-foreground md:text-3xl">{s.title}</h3>
                  <p className="mt-1 text-base font-semibold text-brand">{s.subtitle}</p>
                  <p className="mt-3 text-muted-foreground">{s.body}</p>
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:pt-2">
                  <Link
                    to={`/${s.slug}` as string}
                    className="inline-flex items-center rounded-md border border-brand bg-brand px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-brand-dark"
                  >
                    Explore {s.title} →
                  </Link>
                </div>
              </div>
              {i < stages.length - 1 && (
                <div className="flex justify-center py-2" aria-hidden="true">
                  <div className="h-8 w-px bg-border" />
                </div>
              )}
            </li>
          ))}
        </ol>
      </Section>

      <div className="bg-muted/40">
        <Section>
          <SectionHeader
            eyebrow="Why one lender matters"
            title="Fewer files. Fewer surprises. Faster capital."
            center
          />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Reused underwriting",
                body:
                  "Your entity docs, credit, reserves, and insurance carry from bridge to fix & flip to DSCR — no restarting from zero when it's time to refinance.",
              },
              {
                title: "Priced takeout up front",
                body:
                  "We indicate DSCR takeout terms when the bridge closes so you know your permanent exit before you swing a hammer.",
              },
              {
                title: "One point of contact",
                body:
                  "Same loan officer, same processor, same underwriting team across all three products. No handoffs, no lost context.",
              },
            ].map((c) => (
              <div key={c.title} className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="font-display text-lg font-semibold text-foreground">{c.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow={"Live pricing · DSCR"}
        heading={"What today's DSCR pricing means for your next investor step"}
        paragraphs={[
          "These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property — rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/rates", label: "See today's investor rate bands" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="Journey FAQ" title="How the full-cycle path actually works" />
        <div className="mt-8">
          <FAQ items={journeyFaqs} />
        </div>
      </Section>

      <CTABand
        title="Start the journey. Fund the whole deal, not just the first step."
        body="Send us an address and a business plan — we'll price the bridge and indicate the DSCR takeout in the same pricing feedback."
      />
    <PageOutro />
      </SiteShell>
  );
}
